// ZO Looks section: detail dialogs, variant choice, add to cart and «Solicitar pieza».
// Plain ES module, no dependencies.
const base = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
const ADD_URL = base + '/cart/add.js';
const CART_URL = base + '/cart';
const variantsOf = new WeakMap();
const busy = new WeakSet(); // dialogs with an add request in flight
const REQUEST_KEY = 'zo-looks-request'; // sessionStorage: { look, product } across a native contact POST

function variants(piece) {
  if (!variantsOf.has(piece)) {
    let list = [];
    try { list = JSON.parse(piece.querySelector('[data-zo-variants]').textContent); } catch { /* none */ }
    variantsOf.set(piece, list);
  }
  return variantsOf.get(piece);
}

// { missing: [option names] } while a select is unset, else { variant } (undefined = no such combination).
function resolve(piece) {
  const list = variants(piece);
  const selects = [...piece.querySelectorAll('[data-zo-option]')];
  if (selects.length === 0) return { variant: list[0] };
  const missing = selects.filter((s) => !s.value).map((s) => s.dataset.name);
  if (missing.length) return { missing };
  const chosen = selects.map((s) => s.value);
  return { variant: list.find((v) => chosen.every((c, i) => v.options[i] === c)) };
}

function update(piece) {
  const { missing, variant } = resolve(piece);
  const soldOut = !variants(piece).some((v) => v.available) || (!missing && !(variant && variant.available));
  piece.querySelector('[data-zo-add]').disabled = Boolean(missing) || soldOut || busy.has(piece.closest('dialog'));
  piece.querySelector('[data-zo-state]').textContent = soldOut ? 'Agotado' : '';
  piece.querySelector('[data-zo-request]').hidden = !soldOut;
  if (variant) piece.querySelector('[data-zo-price]').textContent = variant.price;
}

function say(msg, text, cartLink = false) {
  msg.textContent = text;
  if (!cartLink) return;
  const a = document.createElement('a');
  a.href = CART_URL;
  a.textContent = 'Ver carrito';
  msg.append(' · ', a);
}

// While a request is in flight every add button of the dialog is disabled (no double add).
async function add(items, msg, dialog, note = '') {
  busy.add(dialog);
  dialog.querySelectorAll('[data-zo-add], [data-zo-add-look]').forEach((b) => { b.disabled = true; });
  say(msg, 'Añadiendo…');
  // Dawn's own cart UI (cart_type notification or drawer): ask /cart/add.js for its sections in the
  // same call, then let the element repaint itself, exactly as Dawn's product-form.js does.
  const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  const body = { items };
  if (cart && typeof cart.getSectionsToRender === 'function') {
    body.sections = cart.getSectionsToRender().map((s) => s.id);
    body.sections_url = window.location.pathname;
  }
  try {
    const res = await fetch(ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    // A rejected multi-item add may still have changed the cart: refresh either way.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (!res.ok) {
      say(msg, data.description || data.message || 'No se ha podido añadir al carrito.', items.length > 1);
    } else {
      document.dispatchEvent(new CustomEvent('zo-looks:added', { detail: { items } }));
      say(msg, 'Añadido', true);
      // The theme's cart opens above the page, so close the (top-layer) dialog first, unless a note
      // about skipped pieces still has to be read. Only when every requested section came back:
      // a theme that deleted those sections (ZHANNAONA's Dawn has no cart-notification-product /
      // cart-icon-bubble) answers null, and closing would hide the only confirmation the customer has.
      const sectionsOk = Boolean(data.sections) && (body.sections ?? []).every((id) => data.sections[id]);
      if (!note && sectionsOk && typeof cart.renderContents === 'function') {
        try {
          dialog.close();
          cart.renderContents({ ...data, key: data.key ?? data.items?.[0]?.key });
          // Dawn's pubsub.js: `publish` is a global function, PUB_SUB_EVENTS a global const (not on window).
          // eslint-disable-next-line no-undef
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'zo-looks', cartData: data });
        } catch { /* inline «Añadido · Ver carrito» stays */ }
      }
    }
    if (note) msg.append(' ' + note);
  } catch {
    say(msg, 'No se ha podido añadir al carrito. Inténtalo de nuevo.');
  } finally {
    busy.delete(dialog);
    dialog.querySelectorAll('[data-zo-add-look]').forEach((b) => { b.disabled = false; });
    dialog.querySelectorAll('[data-zo-piece]').forEach(update);
  }
}

function addLook(dialog, msg) {
  const items = [];
  const unchosen = [];
  const soldOut = [];
  for (const piece of dialog.querySelectorAll('[data-zo-piece]')) {
    const { missing, variant } = resolve(piece);
    if (!variants(piece).some((v) => v.available)) soldOut.push(piece.dataset.title);
    else if (missing) unchosen.push(piece.dataset.title);
    else if (variant && variant.available) items.push({ id: variant.id, quantity: 1 });
    else soldOut.push(piece.dataset.title);
  }
  // Pieces whose product is missing render without data-zo-piece; they count as sold out too.
  const missingProducts = dialog.querySelectorAll('.zo-looks-piece--missing').length;
  const notes = [];
  if (unchosen.length) notes.push(`Falta elegir opciones en: ${unchosen.join(', ')}.`);
  if (soldOut.length || missingProducts) {
    const names = soldOut.length ? soldOut.join(', ') : '';
    const extra = missingProducts ? `${missingProducts} pieza${missingProducts > 1 ? 's' : ''} no disponible${missingProducts > 1 ? 's' : ''}` : '';
    notes.push(`Agotado: ${[names, extra].filter(Boolean).join(', ')}.`);
  }
  const note = notes.join(' ');
  if (!items.length) return say(msg, note || 'No hay piezas disponibles.');
  return add(items, msg, dialog, note);
}

function play(video) {
  const p = video.play();
  if (p) p.catch(() => { /* autoplay blocked: the poster stays */ });
}

// Row videos below the first row carry no autoplay (it would override preload="none"):
// play them while in view, pause them when out of view.
const inView = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
    for (const e of entries) e.isIntersecting ? play(e.target) : e.target.pause();
  }, { threshold: 0.25 })
  : null;

function openLook(root, id, opener) {
  const dialog = root.querySelector(`[data-zo-dialog="${CSS.escape(id)}"]`);
  if (!dialog) return null;
  const videos = dialog.querySelectorAll('video');
  dialog.addEventListener('close', () => {
    videos.forEach((v) => v.pause());
    if (opener) opener.focus();
  }, { once: true });
  dialog.showModal();
  videos.forEach(play);
  return dialog;
}

function field(box, id, labelText, control) {
  const label = document.createElement('label');
  label.className = 'zo-looks-piece__opt';
  label.htmlFor = id;
  label.textContent = labelText;
  control.id = id;
  control.className = 'zo-looks-piece__select';
  box.append(label, control);
}

// Fills the section's request dialog from a piece (product or missing) and opens it above the look dialog.
function openRequest(root, piece, opener, resuming = false) {
  const rd = root.querySelector('[data-zo-request-dialog]');
  if (!rd) return;
  const form = rd.querySelector('form');
  const look = piece.closest('[data-zo-dialog]');
  const title = piece.dataset.title || 'Pieza';
  form.reset();
  // A stale default_errors block from an earlier native POST belongs to that request only.
  if (!resuming) form.querySelector('.errors')?.remove();
  form.hidden = false;
  rd.querySelector('[data-zo-request-done]').hidden = true;
  form.querySelector('[data-zo-msg]').textContent = '';
  form.querySelector('[type="submit"]').disabled = false;
  rd.querySelector('[data-zo-request-product]').textContent = title;
  rd.dataset.look = look ? look.dataset.zoDialog : '';
  rd.dataset.product = title;
  form.elements['contact[Producto]'].value = title;
  form.elements['contact[Enlace]'].value = piece.dataset.url
    ? new URL(piece.dataset.url, location.origin).href
    : location.origin + location.pathname;
  form.elements['contact[Look]'].value = look ? look.querySelector('.zo-looks-detail__title').textContent.trim() : '';
  // One field per product option, same values, the piece's current choice preselected.
  const box = rd.querySelector('[data-zo-request-options]');
  box.replaceChildren();
  piece.querySelectorAll('[data-zo-option]').forEach((s, i) => {
    const copy = s.cloneNode(true);
    copy.removeAttribute('data-zo-option');
    copy.name = `contact[${s.dataset.name}]`;
    field(box, `${form.id}-opt-${i}`, s.dataset.name, copy);
    copy.value = s.value;
  });
  if (!piece.matches('[data-zo-piece]')) {
    ['Talla', 'Color'].forEach((name, i) => {
      const input = document.createElement('input');
      input.type = 'text';
      input.name = `contact[${name}]`;
      field(box, `${form.id}-opt-${i}`, name, input);
    });
  }
  if (opener) rd.addEventListener('close', () => opener.focus(), { once: true });
  if (!rd.open) rd.showModal();
}

// fetch first (no page reload); only a redirect to ?contact_posted=true counts as sent. Anything else
// (captcha /challenge, a 200 re-render with errors, network error) falls back to the native POST.
async function sendRequest(rd, form) {
  const button = form.querySelector('[type="submit"]');
  const msg = form.querySelector('[data-zo-msg]');
  button.disabled = true;
  msg.textContent = 'Enviando…';
  try {
    const res = await fetch(form.action, { method: 'POST', body: new FormData(form) });
    if (new URL(res.url).searchParams.get('contact_posted') === 'true') {
      form.hidden = true;
      msg.textContent = '';
      rd.querySelector('[data-zo-request-done]').hidden = false;
      return;
    }
  } catch { /* native POST below */ }
  try {
    sessionStorage.setItem(REQUEST_KEY, JSON.stringify({ look: rd.dataset.look, product: rd.dataset.product }));
  } catch { /* storage blocked: the POST still goes through, the dialog just will not reopen */ }
  form.submit(); // bypasses the submit listener: no loop
}

// Back from a native POST: reopen the look, then the request dialog, either saying it was sent
// (?contact_posted=true) or showing the form with Shopify's default_errors output.
function resume(root) {
  const rd = root.querySelector('[data-zo-request-dialog]');
  if (!rd) return;
  const sent = new URL(location.href).searchParams.get('contact_posted') === 'true';
  const errors = rd.querySelector('form .errors');
  if (!sent && !errors) return;
  let saved = null;
  try { saved = JSON.parse(sessionStorage.getItem(REQUEST_KEY)); } catch { /* none */ }
  if (!saved) return;
  try { sessionStorage.removeItem(REQUEST_KEY); } catch { /* ignore */ } // also when the look is gone: no stale key
  const look = saved.look && openLook(root, saved.look, root.querySelector(`[data-zo-look="${CSS.escape(saved.look)}"]`));
  if (!look) return;
  if (!sent) {
    const piece = [...look.querySelectorAll('.zo-looks-piece')].find((p) => p.dataset.title === saved.product);
    if (piece) return openRequest(root, piece, piece.querySelector('[data-zo-request]'), true);
    rd.querySelector('form').elements['contact[Producto]'].value = saved.product || '';
    rd.querySelector('[data-zo-request-product]').textContent = saved.product || '';
    return rd.showModal();
  }
  rd.querySelector('form').hidden = true;
  rd.querySelector('[data-zo-request-product]').textContent = saved.product || '';
  rd.querySelector('[data-zo-request-done]').hidden = false;
  rd.showModal();
}

for (const root of document.querySelectorAll('[data-zo-looks]')) {
  root.querySelectorAll('[data-zo-piece]').forEach(update);
  root.querySelectorAll('.zo-looks-row video:not([autoplay])').forEach((v) => (inView ? inView.observe(v) : play(v)));

  root.addEventListener('change', (e) => {
    const piece = e.target.matches('[data-zo-option]') && e.target.closest('[data-zo-piece]');
    if (piece) update(piece);
  });

  root.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-zo-look]');
    if (opener) {
      openLook(root, opener.dataset.zoLook, opener);
      return;
    }
    const dialog = e.target.closest('dialog');
    if (!dialog) return;
    // Backdrop (the dialog itself has no padding) or the close button.
    if (e.target === dialog || e.target.closest('[data-zo-close]')) return dialog.close();
    const request = e.target.closest('[data-zo-request]');
    if (request) return openRequest(root, request.closest('.zo-looks-piece'), request);
    const one = e.target.closest('[data-zo-add]');
    if (one) {
      const piece = one.closest('[data-zo-piece]');
      const { variant } = resolve(piece);
      if (variant && variant.available) add([{ id: variant.id, quantity: 1 }], piece.querySelector('[data-zo-msg]'), dialog);
      return;
    }
    if (e.target.closest('[data-zo-add-look]')) addLook(dialog, dialog.querySelector('.zo-looks-detail__actions [data-zo-msg]'));
  });

  root.addEventListener('submit', (e) => {
    const rd = e.target.closest('[data-zo-request-dialog]');
    if (!rd) return;
    e.preventDefault();
    sendRequest(rd, e.target);
  });

  resume(root);
}
